<template>
  <modal
    :title="title"
    size="full"
    :open="open"
    @close="closeModal"
  >
    <form>
      <card-image-upload v-model="sale" />

      <text-area-input
        v-model="sale.title"
        label="Title"
      />

      <currency-input
        v-model="sale.price"
        label="Price"
      />

      <date-input
        v-model="sale.date"
        label="Date"
      />

      <select-input
        v-model="sale.platform"
        label="Platform"
        :options="platformOptions"
      />

      <select-input
        v-model="sale.listingType"
        label="Listing Type"
        :options="listingTypeOptions"
      />

      <text-input
        v-model.trim="sale.url"
        type="url"
        label="Sale URL"
      />

      <text-area-input
        v-model="sale.notes"
        label="Notes"
      />

      <checkbox
        v-model="sale.memberVerified"
        label="Member Verified"
      />
    </form>

    <template #footer>
      <align small-gap>
        <btn
          loading-text="Submitting"
          :loading="submitting"
          :disabled="disabled"
          @click="addOrEditSale"
        >
          Submit
        </btn>

        <btn kind="secondary" @click="closeModal">
          Cancel
        </btn>
      </align>
    </template>
  </modal>
</template>

<script>
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import { SELLING_PLATFORM_OPTIONS, LISTING_TYPE_OPTIONS } from '@cardladder/shared/constants';

import { saleService } from '@/services';

import CardImageUpload from '@/cards/CardImageUpload';

const DEFAULT = {
  image: '',
  title: '',
  price: 10000,
  date: new Date(),
  platform: 'eBay',
  listingType: 'BestOffer',
  url: '',
  notes: '',
  memberVerified: false,
};

export default {
  components: {
    CardImageUpload,
  },

  data() {
    return {
      open: false,
      sale: clone(DEFAULT),
      submitting: false,
      platformOptions: SELLING_PLATFORM_OPTIONS,
      listingTypeOptions: LISTING_TYPE_OPTIONS,
    };
  },

  mounted() {
    this.$bus.$on('sales-history-item-edit', this.handle);
  },

  beforeDestroy() {
    this.$bus.$off('sales-history-item-edit', this.handle);
  },

  computed: {
    title() {
      return this.isEditing ? 'Edit Sale' : 'Add Sale';
    },

    isEditing() {
      return Boolean(this.sale && this.sale.itemId);
    },

    disabled() {
      if (typeof this.sale.price !== 'number' || this.sale.price <= 0 || !this.sale.title || !this.sale.date || !this.sale.image || !this.sale.platform || !this.sale.listingType) {
        return true;
      }

      if (this.sale.platform !== 'Private' && !this.sale.url) {
        return true;
      }

      return false;
    },
  },

  methods: {
    handle(sale) {
      if (sale) {
        this.sale = clone(sale);
      }

      this.open = true;
    },

    async addOrEditSale() {
      if (this.disabled) {
        return;
      }

      this.submitting = true;

      try {
        const itemId = await saleService.createOrUpdateSalesHistorySale(this.sale);

        this.$toasted.success(`${this.isEditing ? 'Updated' : 'Added'} the sale!`);
        this.closeModal();

        if (!this.isEditing) {
          setTimeout(() => {
            this.$router.replace({ path: '/sales-history', query: { ...this.$route.query, saleId: itemId } }).catch(() => {});
          }, 3000);
        }
      } catch (e) {
        this.$toasted.error(`Failed to ${this.isEditing ? 'edit' : 'add'} the sale!`);
        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    reset() {
      this.sale = clone(DEFAULT);
    },

    closeModal() {
      this.reset();
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(1, 1fr);
}

form {
  max-width: 1000px;
  margin: 0 auto;
}
</style>
